import { CLIENTS_ENDPOINT } from "../../../../consts";
import { useCRUD } from "../../../../api/crud";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { Client } from "../../../../types/clients";
import { AddressDisplay } from "../../../../components/ui/addressDisplay/addressDisplay.component";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { useTranslation } from "react-i18next";
import {
  DetailGrid,
  DetailHeader,
  DetailMetadata,
  DetailPage,
  DetailRow,
  DetailSection,
} from "../../../../components/ui/detailView";
import { useHasPermission } from "../../../../contexts/auth";

type ClientBasicInfoProps = {
  previewId?: string | number;
};

export const ClientBasicInfo = ({ previewId }: ClientBasicInfoProps) => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { get } = useCRUD(CLIENTS_ENDPOINT);
  const [client, setClient] = useState<Client | null>(null);
  const canViewClients = useHasPermission("settlement.view_client");
  const entityId = previewId ?? id;

  useEffect(() => {
    const fetchClient = async () => {
      if (entityId) {
        const response = await get(entityId);
        setClient(response.data);
      }
    };

    const handleClientUpdated = () => {
      fetchClient();
    };

    window.addEventListener("clientUpdated", handleClientUpdated);
    fetchClient();
    return () => {
      window.removeEventListener("clientUpdated", handleClientUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  if (!canViewClients) {
    return null;
  }

  if (!client) return <DetailPage loading />;

  return (
    <DetailPage>
      <DetailHeader
        icon={<BusinessOutlinedIcon />}
        title={client.short_name || client.name}
        subtitle={client.short_name ? client.name : client.nip && `${t("NIP")} ${client.nip}`}
      />

      <DetailGrid>
        <DetailSection title={t("Basic Information")}>
          <DetailRow label={t("Name")} value={client.name} />
          {client.short_name != null && client.short_name !== "" && (
            <DetailRow label={t("Short name")} value={client.short_name} />
          )}
        </DetailSection>

        <DetailSection title={t("Identification Numbers")}>
          <DetailRow label={t("PESEL")} value={client.pesel} />
          <DetailRow label={t("NIP")} value={client.nip} />
          <DetailRow label={t("REGON")} value={client.regon} />
          <DetailRow label={t("KRS")} value={client.krs} />
        </DetailSection>

        <AddressDisplay address={client.address} title={t("Address Information")} withDetails />

        <DetailSection title={t("Contact Information")}>
          <DetailRow label={t("Phone Number")} value={client.address.phone_number} />
          <DetailRow label={t("Email")} value={client.address.email} />
          {client.address.email_invoices && (
            <DetailRow label={t("Invoices Email")} value={client.address.email_invoices} />
          )}
          <DetailRow
            label={t("Email for counter reminders")}
            value={client.address.email_counter_reminders}
          />
        </DetailSection>
      </DetailGrid>

      <DetailMetadata createdAt={client.created_at} updatedAt={client.updated_at} />
    </DetailPage>
  );
};
